import { Component, input, output, computed, signal, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { InteractionConfig, InteractionOption, AI_CHAT_CONFIG } from '../ai-chat.config';

/**
 * @file chat-interaction-widget.component.ts
 * @description A dedicated interactive widget to handle structured AI requests (e.g., Radio/Checkbox selections).
 * Intelligently binds arrays to UI elements and dispatches user selections back to the chat context.
 */
@Component({
  selector: 'rapider-chat-interaction-widget',
  standalone: true,
  imports: [CommonModule, FormsModule, NzRadioModule, NzCheckboxModule, NzButtonModule],
  template: `
    <div class="mt-2 w-full flex flex-col gap-2">
      @if (config().type === 'radio') {
        @for (opt of config().options; track opt.value) {
          <div (click)="!isResponded() ? handleRadioSelect(opt.value) : null"
               class="w-full flex items-center justify-between px-4 py-3 border rounded-xl transition-all duration-200 cursor-pointer group"
               [ngClass]="radioValue() === opt.value || (isResponded() && selectedValues().includes(opt.value)) ? 'bg-primary/5 border-primary text-primary shadow-sm' : 'bg-component-inner border-secondary-border hover:border-primary text-primary-text hover:shadow-sm'">
            <span class="text-sm font-medium">{{ opt.title }}</span>
            @if (radioValue() === opt.value || (isResponded() && selectedValues().includes(opt.value))) {
              <i class="fas fa-check-circle text-[#27272A] dark:text-[#D4D4D8] text-lg"></i>
            } @else {
              <i class="far fa-circle text-secondary-text text-lg group-hover:text-primary-text transition-colors"></i>
            }
          </div>
        }
      } @else {
        @for (opt of config().options; track opt.value) {
          <div (click)="!isResponded() ? toggleCheckbox(opt.value) : null"
               class="w-full flex items-center justify-between px-4 py-3 border rounded-xl transition-all duration-200 cursor-pointer group"
               [ngClass]="checkboxValues()[opt.value] || (isResponded() && selectedValues().includes(opt.value)) ? 'bg-alt-background border-[#27272A]/30 dark:border-[#D4D4D8]/30 text-primary-text shadow-none' : 'bg-component-inner border-secondary-border hover:border-primary-border text-primary-text hover:shadow-none'">
            <span class="text-sm font-medium">{{ opt.title }}</span>
            <label nz-checkbox
                   [ngModel]="checkboxValues()[opt.value] || (isResponded() && selectedValues().includes(opt.value))"
                   (ngModelChange)="onCheckboxChange(opt.value, $event)"
                   [nzDisabled]="isResponded()"
                   (click)="$event.stopPropagation()"
                   class="m-0 pointer-events-none">
            </label>
          </div>
        }
      }

      @if (!isResponded() && config().type !== 'radio') {
        <div class="flex items-center justify-end gap-2 mt-1">
          <button nz-button nzType="default" (click)="onSkip.emit()" i18n="@@aiChat.interactionSkipBtn" class="border-secondary-border text-secondary-text shadow-none hover:text-primary-text hover:border-secondary-border transition-colors rounded-[6px] font-medium py-1 px-3 text-[13px]">{{ texts.interactionSkipBtn }}</button>
          <button nz-button (click)="handleSubmit()" [disabled]="!hasSelection()" i18n="@@aiChat.interactionSubmitBtn" class="bg-[#27272A] hover:bg-[#3F3F46] dark:bg-[#D4D4D8] dark:hover:bg-[#E4E4E7] text-[#FAFAFA] dark:text-[#18181B] border border-black/10 dark:border-white/10 shadow-xs rounded-[6px] font-medium py-1 px-3.5 text-[13px] transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer">{{ texts.interactionSubmitBtn }}</button>
        </div>
      } @else if (isResponded()) {
         <div class="flex items-center justify-end mt-1">
            <span class="text-xs text-success font-bold flex items-center gap-1">
              <i class="fas fa-check-circle"></i> <ng-container i18n="@@aiChat.interactionSubmitted">{{ texts.interactionSubmitted }}</ng-container>
            </span>
         </div>
      }
    </div>
  `
})
export class ChatInteractionWidgetComponent {
  public config = input.required<InteractionConfig>();
  public messageId = input.required<string>();
  public isResponded = input.required<boolean>();
  public selectedValues = input<string[]>([]);

  public onSubmit = output<InteractionOption[]>();
  public onSkip = output<void>();

  public texts = AI_CHAT_CONFIG.texts;

  // Local UI tracking signals
  public radioValue = signal<string | null>(null);
  public checkboxValues = signal<Record<string, boolean>>({});

  constructor() {
    // Hydrate the form models securely based on input arrays
    effect(() => {
      const vals = this.selectedValues();
      if (vals && vals.length > 0) {
        if (this.config().type === 'radio') {
          this.radioValue.set(vals[0]);
        } else {
          const map: Record<string, boolean> = {};
          vals.forEach(v => map[v] = true);
          this.checkboxValues.set(map);
        }
      }
    }, { allowSignalWrites: true });
  }

  /**
   * Computed flag confirming if the user has made at least one valid selection.
   */
  public hasSelection = computed(() => {
    if (this.config().type === 'radio') {
      return this.radioValue() !== null;
    } else {
      return Object.values(this.checkboxValues()).some(v => v);
    }
  });

  /**
   * Maps individual checkbox changes into the internal state dictionary safely.
   * @param {string} val - The option value.
   * @param {boolean} checked - The new checked status.
   */
  public onCheckboxChange(val: string, checked: boolean): void {
    this.checkboxValues.update(prev => ({ ...prev, [val]: checked }));
  }

  /**
   * Toggles the checkbox state natively when clicking the full row wrapper.
   * @param {string} val - The clicked option value.
   */
  public toggleCheckbox(val: string): void {
    const current = this.checkboxValues()[val];
    this.onCheckboxChange(val, !current);
  }

  /**
   * Handles instant selection and submission for radio (single-select) interactions.
   * @param {string} val - The selected option value.
   */
  public handleRadioSelect(val: string): void {
    this.radioValue.set(val);
    this.handleSubmit();
  }

  /**
   * Gathers the final selections and broadcasts the array back to the parent.
   */
  public handleSubmit(): void {
    let selected: InteractionOption[] = [];
    if (this.config().type === 'radio') {
      const matched = this.config().options.find(o => o.value === this.radioValue());
      if (matched) selected.push(matched);
    } else {
      const map = this.checkboxValues();
      selected = this.config().options.filter(o => map[o.value]);
    }
    this.onSubmit.emit(selected);
  }
}